import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../../../../backend/firebaseConfig";
import Text from "../../atoms/text/Text";
import "./MedicalHistory.css";
import { Box, Stack,  } from "@mui/system";
import Icon from "../../atoms/icon/Icon";
import { Divider } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FavoriteIcon from '@mui/icons-material/Favorite';


const dentalHistoryQuestions = [
  { key: "bleedingGums", text: "Do your gums bleed when you brush or floss?" },
  { key: "sensitiveTeeth", text: "Are your teeth sensitive to hot, cold, sweets, or pressure?" },
  { key: "flossCatch", text: "Does food or floss catch between your teeth?" },
  { key: "dryMouth", text: "Is your mouth often dry?" },
  { key: "periodontalTreatment", text: "Have you had periodontal (gum) treatment?" },
  { key: "orthodonticTreatment", text: "Have you had orthodontic treatment (braces)?" },
  { key: "mouthInjury", text: "Have you had serious injury to your head or mouth?" },
  { key: "jawClicking", text: "Do you have clicking, popping, or other discomfort in your jaw?" },
  { key: "dentalTreatmentProblems", text: "Have you had any problems related to dental treatment?" },
  { key: "currentDentalPain", text: "Are you currently experiencing dental pain or discomfort?" },
  { key: "ulcers", text: "Do you experience frequent ulcers in your mouth?" },
  { key: "sores", text: "Do you currently have any sores or ulcers in your mouth?" },
  { key: "energeticSports", text: "Do you participate in energetic sports or activities?" },
  { key: "grindTeeth", text: "Do you grind your teeth?" },
  { key: "dentures", text: "Do you wear dentures or partial dentures?" },
  { key: "fluoridatedHome", text: "Is your home water fluoridated?" },
  { key: "bottledWater", text: "Do you frequently drink bottled water?" },
];

const medicalHistoryQuestions = [
  { key: "physicianCare", text: "Are you under a physician's care now?" },
  { key: "hospitalizedOperation", text: "Have you ever been hospitalized or had a major operation?" },
  { key: "seriousHeadNeckInjury", text: "Have you ever had a serious head or neck injury?" },
  { key: "medicationsPillsDrugs", text: "Are you taking any medications, pills, or drugs?" },
  { key: "phenFenRedux", text: "Do you take, or have you taken, Phen-Fen or Redux?" },
  { key: "fosamaxBonivaActonel", text: "Have you ever taken Fosamax, Boniva, Actonel or any other medications containing bisphosphonates?" },
  { key: "specialDiet", text: "Are you on a special diet?" },
  { key: "tobaccoProducts", text: "Do you use any tobacco products?" },
  { key: "controlledSubstances", text: "Do you use any controlled substances?" },
  { key: "vapeECigarettes", text: "Do you Vape or use E-cigarettes?" },
  { key: "marijuanaCannabis", text: "Do you use any forms of Marijuana, Pot, Weed or Cannabis?" },
  { key: "pastTobaccoUse", text: "Have you used any tobacco products, controlled substances, vape or any forms of marijuana in the past?" },
];

const healthConditions = [
  { key: "AIDS_HIV_positive", label: "AIDS/HIV positive", part: "body" },
  { key: "Alzheimers_disease", label: "Alzheimer's disease", part: "brain"  },
  { key: "Anaphylaxis", label: "Anaphylaxis", part: "body" },
  { key: "Anemia", label: "Anemia", part: "blood" },
  { key: "Angina", label: "Angina", part: "heart" },
  { key: "Arthritis_Gout", label: "Arthritis/Gout", part: "joint" },
  { key: "Artificial_heart_valve", label: "Artificial heart valve", part: "heart" },
  { key: "Artificial_joint", label: "Artificial joint", part: "joint" },
  { key: "Asthma", label: "Asthma", part: "lungs" },
  { key: "Blood_disease", label: "Blood disease", part: "blood" },
  { key: "Blood_transfusion", label: "Blood transfusion", part: "blood" },
  { key: "Breathing_problems", label: "Breathing problems", part: "lungs" },
  { key: "Bruise_easily", label: "Bruise easily", part: "body" },
  { key: "Cancer", label: "Cancer", part: "body" },
  { key: "Chemotherapy", label: "Chemotherapy", part: "body" },
  { key: "Chest_pains", label: "Chest pains", part: "heart" },
  { key: "Cold_sores_fever_blisters", label: "Cold sores/fever blisters", part: "arm" },
  { key: "Congenital_heart_disorder", label: "Congenital heart disorder", part: "heart" },
  { key: "Convulsions", label: "Convulsions", part: "body" },
  { key: "Cortisone_medicine", label: "Cortisone medicine", part: "pill" },
  { key: "Diabetes", label: "Diabetes", part: "injection" },
  { key: "Drug_addiction", label: "Drug addiction", part: "injection" },
  { key: "Easily_winded", label: "Easily winded", part: "lungs" },
  { key: "Emphysema", label: "Emphysema", part: "lungs" },
  { key: "Epilepsy_or_seizures", label: "Epilepsy or seizures", part: "body" },
  { key: "Excessive_bleeding", label: "Excessive bleeding", part: "blood" },
  { key: "Fainting_or_dizziness", label: "Fainting or dizziness", part: "brain" },
  { key: "Frequent_cough", label: "Frequent cough", part: "cough" },
  { key: "Frequent_diarrhea", label: "Frequent diarrhea", part: "stomach" },
  { key: "Frequent_headaches", label: "Frequent headaches", part: "brain" },
  { key: "Genital_herpes", label: "Genital herpes", part: "body" },
  { key: "Glaucoma", label: "Glaucoma", part: "eye" },
  { key: "Hay_fever", label: "Hay fever", part: "nose" },
  { key: "Heart_attack_Failure", label: "Heart attack/Failure", part: "heart" },
  { key: "Heart_murmur", label: "Heart murmur", part: "heart" },
  { key: "Heart_pacemaker", label: "Heart pacemaker", part: "heart" },
  { key: "Heart_trouble_Disease", label: "Heart trouble/Disease", part: "heart" },
  { key: "Hemophilia", label: "Hemophilia", part: "blood" },
  { key: "Hepatitis_A", label: "Hepatitis A", part: "liver" },
  { key: "Hepatitis_B_or_C", label: "Hepatitis B or C", part: "liver" },
  { key: "Herpes", label: "Herpes", part: "mouth" },
  { key: "High_blood_pressure", label: "High blood pressure", part: "blood" },
  { key: "High_cholesterol", label: "High cholesterol", part: "artery" },
  { key: "Hives_or_rash", label: "Hives or rash", part: "skin" },
  { key: "Hypoglycemia", label: "Hypoglycemia", part: "brain" },
  { key: "Irregular_heartbeat", label: "Irregular heartbeat", part: "heart" },
  { key: "Kidney_problems", label: "Kidney problems", part: "kidney" },
  { key: "Leukemia", label: "Leukemia", part: "blood" },
  { key: "Liver_disease", label: "Liver disease", part: "liver" },
  { key: "Low_blood_pressure", label: "Low blood pressure", part: "blood" },
  { key: "Lung_disease", label: "Lung disease", part: "lungs" },
  { key: "Mitral_valve_prolapse", label: "Mitral valve prolapse", part: "heart" },
  { key: "Osteoporosis", label: "Osteoporosis", part: "bone" },
  { key: "Pain_in_jaw_joints", label: "Pain in jaw joints", part: "jaw" },
  { key: "Parathyroid_disease", label: "Parathyroid disease", part: "neck" },
  { key: "Psychiatric_care", label: "Psychiatric care", part: "brain" },
  { key: "Radiation_treatment", label: "Radiation treatment", part: "body" },
  { key: "Recent_weight_loss", label: "Recent weight loss", part: "body" },
  { key: "Renal_dialysis", label: "Renal dialysis", part: "kidney" },
  { key: "Rheumatic_fever", label: "Rheumatic fever", part: "nose" },
  { key: "Rheumatism", label: "Rheumatism", part: "joint" },
  { key: "Scarlet_fever", label: "Scarlet fever", part: "skin" },
  { key: "Shingles", label: "Shingles", part: "body" },
  { key: "Sickle_cell_disease", label: "Sickle cell disease", part: "blood" },
  { key: "Sinus_trouble", label: "Sinus trouble", part: "nose" },
  { key: "Spinal_bifida", label: "Spinal bifida", part: "spine" },
  { key: "Stomach_Intestinal_disease", label: "Stomach/Intestinal disease", part: "stomach" },
  { key: "Stroke", label: "Stroke", part: "brain" },
  { key: "Swelling_of_limbs", label: "Swelling of limbs", part: "limb" },
  { key: "Thyroid_disease", label: "Thyroid disease", part: "neck" },
  { key: "Tonsillitis", label: "Tonsillitis", part: "neck" },
  { key: "Tuberculosis", label: "Tuberculosis", part: "lungs" },
  { key: "Tumors_or_growths", label: "Tumors or growths", part: "body" },
  { key: "Ulcers", label: "Ulcers", part: "stomach" },
  { key: "Venereal_disease", label: "Venereal disease", part: "body" },
  { key: "Yellow_Jaundice", label: "Yellow Jaundice", part: "liver" },
];

const initialDentalHistory = Object.fromEntries(dentalHistoryQuestions.map(q => [q.key, { answer: "", explanation: "" }]));
const initialMedicalHistory = Object.fromEntries(medicalHistoryQuestions.map(q => [q.key, { answer: "", explanation: "" }]));
const initialHealthConditions = Object.fromEntries(healthConditions.map(c => [c.key, ""]));

const MedicalHistory = forwardRef(({ patientId, searchTerm = "" }, ref) => {
  const [dentalHistory, setDentalHistory] = useState(initialDentalHistory);
  const [medicalHistory, setMedicalHistory] = useState(initialMedicalHistory);
  const [medications, setMedications] = useState([{ name: "", product: "", frequency: "", type: "prescription" }]);
  const [healthHistory, setHealthHistory] = useState(initialHealthConditions);
  const [otherNotes, setOtherNotes] = useState("");
  const [otherPiercings, setOtherPiercings] = useState("");
  const [medMenuOpenIdx, setMedMenuOpenIdx] = useState(null);
  const medMenuRef = React.useRef();

  const selectedHealthCount = React.useMemo(() =>
    Object.values(healthHistory).filter((v) => v === "yes").length,
    [healthHistory]
  );

  // Fetching from Firestore
  useEffect(() => {
    if (patientId) {
      // Read healthHistory from the user document
      const userRef = doc(db, "users", patientId);
      const unsubscribe = onSnapshot(userRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const hh = data.healthHistory || {};
          setDentalHistory(hh.dentalHistory || initialDentalHistory);
          setMedicalHistory(hh.medicalHistory || initialMedicalHistory);
          setMedications(hh.medications || [{ name: "", product: "", frequency: "", type: "prescription" }]);
          setHealthHistory(hh.healthConditions || initialHealthConditions);
          setOtherNotes(hh.explanation || "");
          setOtherPiercings(hh.others || "");
        }
      }, (error) => {
        // Silently handle permission errors during sign-out
        if (error.code === 'permission-denied') {
          console.log('MedicalHistory listener stopped due to sign-out');
          return;
        }
        console.error('Error fetching medical history:', error);
      });
      return () => unsubscribe();
    }
  }, [patientId]);

  // Add click outside handler for menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (medMenuRef.current && !medMenuRef.current.contains(event.target)) {
        setMedMenuOpenIdx(null);
      }
    }
    if (medMenuOpenIdx !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [medMenuOpenIdx]);

  // Handlers
  const handleDentalChange = (key, field, value) => {
    setDentalHistory(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };
  const handleMedicalChange = (key, field, value) => {
    setMedicalHistory(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };
  const handleMedicationChange = (idx, field, value) => {
    setMedications(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };
  const addMedicationRow = () => {
    setMedications(prev => [...prev, { name: "", product: "", frequency: "", type: "prescription" }]);
  };
  const removeMedicationRow = (idx) => {
    setMedications(prev => prev.filter((_, i) => i !== idx));
  };
  const handleHealthHistoryChange = (key, value) => {
    setHealthHistory(prev => ({ ...prev, [key]: value }));
  };

  // Save to Firestore
  const handleSave = async () => {
    // Ensure all unanswered questions are saved as 'no'
    const completedDentalHistory = {};
    Object.entries(dentalHistory).forEach(([key, value]) => {
      completedDentalHistory[key] = {
        answer: value.answer === "yes" ? "yes" : "no",
        explanation: value.explanation || ""
      };
    });

    const completedMedicalHistory = {};
    Object.entries(medicalHistory).forEach(([key, value]) => {
      completedMedicalHistory[key] = {
        answer: value.answer === "yes" ? "yes" : "no",
        explanation: value.explanation || ""
      };
    });

    const completedHealthConditions = {};
    Object.entries(healthHistory).forEach(([key, value]) => {
      completedHealthConditions[key] = value === "yes" ? "yes" : "no";
    });

    try {
      await setDoc(doc(db, "users", patientId), {
        healthHistory: {
          dentalHistory: completedDentalHistory,
          medicalHistory: completedMedicalHistory,
          medications,
          healthConditions: completedHealthConditions,
          others: otherPiercings,
          explanation: otherNotes,
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving medical history:", error);
      alert("Failed to save medical history.");
    }
  };

  // Expose handleSave via ref and add callback for closing modal
  useImperativeHandle(ref, () => ({
    save: async () => {
      await handleSave();
      // Return a promise that resolves after save completes
      return Promise.resolve();
    }
  }));

  // Filter health conditions based on search term
  const filteredHealthConditions = React.useMemo(() => {
    if (!searchTerm.trim()) return healthConditions;
    const query = searchTerm.toLowerCase();
    return healthConditions.filter(cond =>
      cond.label.toLowerCase().includes(query) ||
      cond.part.toLowerCase().includes(query)
    );
  }, [searchTerm]);

  // Calculate if all filtered items are selected
  const isAllSelectedFiltered = React.useMemo(() => {
    if (filteredHealthConditions.length === 0) return false;
    return filteredHealthConditions.every(cond => healthHistory[cond.key] === "yes");
  }, [filteredHealthConditions, healthHistory]);

  // Handle select/deselect all
  const handleSelectAllChange = (selectAll) => {
    const newHealthHistory = { ...healthHistory };
    filteredHealthConditions.forEach(cond => {
      newHealthHistory[cond.key] = selectAll ? "yes" : "";
    });
    setHealthHistory(newHealthHistory);
  };

  return (
      <Box
        sx={{
          width: "100%",
        }}>

        <Stack
          direction="row"
        sx={{
          padding: "10px 0",
          justifyContent: "space-between",
        }}>
          <Stack
          direction="column"
          sx={{
            mb: "5px",
          }}
          spacing={{
            xs : 0,
            sm : 0,
            md : 0,
            lg : 0,
            xl : 0,
          }}
          >
          <Text
            text="Health Conditions"
            variant="serviceTitle"/>
          
          <Text
            text={`Please select the conditions (${selectedHealthCount} selected)`}
            variant="overlayTimeDef"/>

          </Stack>
          
          {/* Select All / Deselect All */}
        <Box
          onClick={() => handleSelectAllChange(!isAllSelectedFiltered)}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            gap: "8px"
          }}
        >
          <Checkbox
            checked={isAllSelectedFiltered}
            onChange={(e) => handleSelectAllChange(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            sx={{
              '&.Mui-checked': {
                color: '#2C6785'
              }
            }}
          />
          <Text
            text={isAllSelectedFiltered ? "Deselect All" : "Select All"}
            variant="selectAll"
          />
        </Box>

          {/* <Divider/> */}
        </Stack>

        

<Box
  sx={{
    flexWrap: "wrap",
    gap: "10px",
    maxHeight: "300px",
    overflowY: "auto",
    padding: "0",
    pb: "10px",
  }}
>
  {filteredHealthConditions.map((cond) => {
    const isSelected = healthHistory[cond.key] === "yes";

    return (
      <Box
        key={cond.key}
        onClick={() =>
          handleHealthHistoryChange(cond.key, isSelected ? "" : "yes")
        }
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          alignContent: "center",
          justifyItems: "center",
          flexDirection: "row",
          // padding: "10px 15px",
          padding: "5px 10px",
          // borderRadius: "8px",
          cursor: "pointer",
          gap: "8px",
          fontSize: "14px",
          borderBottom: "1px solid rgba(89, 84, 74, .5)",
          // border: isSelected
          //   ? "1.5px solid #C5A572"
          //   : "1.5px solid rgba(197, 165, 114, .4)",
          // boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
          backgroundColor: "transparent",
          color: "#2C251E",
          ":hover": {
            backgroundColor: "rgba(89, 84, 74, .1)",
          }
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          ><Box
                sx={{
                  width: "30px",
                  height: "30px",
                  // border: "1px solid black",
                  borderRadius: "8px",
                  boxShadow: '0 1px 5px rgba(0,0,0,0.3)',
                }}>
              <Icon
                src={
                  cond.part === "body" ? require("../../../../assets/icons/body.png") : 
                  cond.part === "brain" ? require("../../../../assets/icons/brain.png") : 
                  cond.part === "blood" ? require("../../../../assets/icons/blood.png") : 
                  cond.part === "heart" ? require("../../../../assets/icons/med_heart.png") : 
                  cond.part === "joint" ? require("../../../../assets/icons/joint.png") : 
                  cond.part === "lungs" ? require("../../../../assets/icons/lungs.png") : 
                  cond.part === "chest" ? require("../../../../assets/icons/torso.png") : 
                  cond.part === "arm" ? require("../../../../assets/icons/arm.png") : 
                  cond.part === "pill" ? require("../../../../assets/icons/pill.png") : 
                  cond.part === "injection" ? require("../../../../assets/icons/injection.png") : 
                  cond.part === "cough" ? require("../../../../assets/icons/cough.png") : 
                  cond.part === "stomach" ? require("../../../../assets/icons/stomach.png") : 
                  cond.part === "eye" ? require("../../../../assets/icons/eye.png") : 
                  cond.part === "nose" ? require("../../../../assets/icons/runny_nose.png") : 
                  cond.part === "liver" ? require("../../../../assets/icons/liver.png") : 
                  cond.part === "mouth" ? require("../../../../assets/icons/mouth.png") : 
                  cond.part === "artery" ? require("../../../../assets/icons/artery.png") : 
                  cond.part === "skin" ? require("../../../../assets/icons/skin.png") : 
                  cond.part === "kidney" ? require("../../../../assets/icons/kidney.png") : 
                  cond.part === "bone" ? require("../../../../assets/icons/bone.png") : 
                  cond.part === "jaw" ? require("../../../../assets/icons/jaw.png") : 
                  cond.part === "neck" ? require("../../../../assets/icons/neck.png") : 
                  cond.part === "spine" ? require("../../../../assets/icons/spine.png") : 
                  cond.part === "limb" ? require("../../../../assets/icons/limb.png") : 
                  require("../../../../assets/icons/body.png")}
                size="small-no-margin-x"/>
                </Box>
              {/* <FavoriteIcon sx={{ width: 20, height: 20 }} /> */}
              <Box>{cond.label}</Box>
              </Stack>
        <FormControlLabel
          // onClick={(e) => {
          //   e.stopPropagation();
          //   handleHealthHistoryChange(cond.key, isSelected ? "" : "yes");
          // }}
          
          sx={{
            alignItems: "center"
          }}
          control={
            <Checkbox
              checked={isSelected}
              onClick={(e) => {
                e.stopPropagation();
                handleHealthHistoryChange(cond.key, isSelected ? "" : "yes");
              }}
              sx={{
                '&.Mui-checked': {
                  color: '#2C6785'
                }
              }}
            />
          }
        />

      </Box>
    );
  })}
  {filteredHealthConditions.length === 0 && searchTerm.trim() && (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        color: "#9F4223",
        fontSize: "14px",
        textAlign: "center"
      }}
    >
      No health conditions found matching "{searchTerm}"
    </Box>
  )}
</Box>

      </Box>
  );
});

MedicalHistory.displayName = 'MedicalHistory';

export default MedicalHistory;